/**
 * Font Configuration
 *
 * Available Google Fonts for the application.
 * All fonts are loaded via Google Fonts in index.html.
 */

export interface FontOption {
  name: string;
  displayName: string;
  category: 'sans-serif' | 'serif' | 'display';
  fontFamily: string;
}

/**
 * Available fonts - 25 carefully selected Google Fonts
 */
export const availableFonts: FontOption[] = [
  // System Default
  {
    name: 'system',
    displayName: 'System Default',
    category: 'sans-serif',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },

  // Modern Sans-Serif (Most Popular)
  {
    name: 'inter',
    displayName: 'Inter',
    category: 'sans-serif',
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
  },
  {
    name: 'roboto',
    displayName: 'Roboto',
    category: 'sans-serif',
    fontFamily: '"Roboto", -apple-system, BlinkMacSystemFont, sans-serif',
  },
  {
    name: 'open-sans',
    displayName: 'Open Sans',
    category: 'sans-serif',
    fontFamily: '"Open Sans", -apple-system, BlinkMacSystemFont, sans-serif',
  },
  {
    name: 'lato',
    displayName: 'Lato',
    category: 'sans-serif',
    fontFamily: '"Lato", -apple-system, BlinkMacSystemFont, sans-serif',
  },
  {
    name: 'montserrat',
    displayName: 'Montserrat',
    category: 'sans-serif',
    fontFamily: '"Montserrat", -apple-system, BlinkMacSystemFont, sans-serif',
  },
  {
    name: 'poppins',
    displayName: 'Poppins',
    category: 'sans-serif',
    fontFamily: '"Poppins", -apple-system, BlinkMacSystemFont, sans-serif',
  },
  {
    name: 'nunito',
    displayName: 'Nunito',
    category: 'sans-serif',
    fontFamily: '"Nunito", -apple-system, BlinkMacSystemFont, sans-serif',
  },
  {
    name: 'raleway',
    displayName: 'Raleway',
    category: 'sans-serif',
    fontFamily: '"Raleway", -apple-system, BlinkMacSystemFont, sans-serif',
  },
  {
    name: 'source-sans',
    displayName: 'Source Sans 3',
    category: 'sans-serif',
    fontFamily: '"Source Sans 3", -apple-system, BlinkMacSystemFont, sans-serif',
  },
  {
    name: 'work-sans',
    displayName: 'Work Sans',
    category: 'sans-serif',
    fontFamily: '"Work Sans", -apple-system, BlinkMacSystemFont, sans-serif',
  },

  // Professional/Corporate
  {
    name: 'ibm-plex',
    displayName: 'IBM Plex Sans',
    category: 'sans-serif',
    fontFamily: '"IBM Plex Sans", -apple-system, BlinkMacSystemFont, sans-serif',
  },
  {
    name: 'outfit',
    displayName: 'Outfit',
    category: 'sans-serif',
    fontFamily: '"Outfit", -apple-system, BlinkMacSystemFont, sans-serif',
  },
  {
    name: 'plus-jakarta',
    displayName: 'Plus Jakarta Sans',
    category: 'sans-serif',
    fontFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, sans-serif',
  },
  {
    name: 'dm-sans',
    displayName: 'DM Sans',
    category: 'sans-serif',
    fontFamily: '"DM Sans", -apple-system, BlinkMacSystemFont, sans-serif',
  },
  {
    name: 'archivo',
    displayName: 'Archivo',
    category: 'sans-serif',
    fontFamily: '"Archivo", -apple-system, BlinkMacSystemFont, sans-serif',
  },

  // Friendly/Rounded
  {
    name: 'rubik',
    displayName: 'Rubik',
    category: 'sans-serif',
    fontFamily: '"Rubik", -apple-system, BlinkMacSystemFont, sans-serif',
  },
  {
    name: 'mulish',
    displayName: 'Mulish',
    category: 'sans-serif',
    fontFamily: '"Mulish", -apple-system, BlinkMacSystemFont, sans-serif',
  },
  {
    name: 'quicksand',
    displayName: 'Quicksand',
    category: 'sans-serif',
    fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, sans-serif',
  },
  {
    name: 'cabin',
    displayName: 'Cabin',
    category: 'sans-serif',
    fontFamily: '"Cabin", -apple-system, BlinkMacSystemFont, sans-serif',
  },
  {
    name: 'karla',
    displayName: 'Karla',
    category: 'sans-serif',
    fontFamily: '"Karla", -apple-system, BlinkMacSystemFont, sans-serif',
  },

  // Modern/Geometric
  {
    name: 'manrope',
    displayName: 'Manrope',
    category: 'sans-serif',
    fontFamily: '"Manrope", -apple-system, BlinkMacSystemFont, sans-serif',
  },
  {
    name: 'red-hat',
    displayName: 'Red Hat Display',
    category: 'display',
    fontFamily: '"Red Hat Display", -apple-system, BlinkMacSystemFont, sans-serif',
  },
  {
    name: 'space-grotesk',
    displayName: 'Space Grotesk',
    category: 'display',
    fontFamily: '"Space Grotesk", -apple-system, BlinkMacSystemFont, sans-serif',
  },
  {
    name: 'figtree',
    displayName: 'Figtree',
    category: 'sans-serif',
    fontFamily: '"Figtree", -apple-system, BlinkMacSystemFont, sans-serif',
  },

  // Serif (for formal/editorial look)
  {
    name: 'merriweather',
    displayName: 'Merriweather',
    category: 'serif',
    fontFamily: '"Merriweather", Georgia, serif',
  },
];

/**
 * Get font by name
 */
export const getFont = (name: string): FontOption => {
  return availableFonts.find((f) => f.name === name) || availableFonts[0];
};

/**
 * Get font family string by name
 */
export const getFontFamily = (name: string): string => {
  return getFont(name).fontFamily;
};

export type FontName = typeof availableFonts[number]['name'];
